import React, { useEffect, useRef, useState } from 'react'
import { supabase, AUTO_REFRESH_ENABLED } from '../supabaseClient'

// Admin inbox for the support chats students, teachers and sales open from
// their dashboards (SupportChat.js). Each person has one room,
// `support_<userId>`; the admin's reply goes into the same room, so it shows
// up in that person's chat, and they also get a notification.

const ROLE_COLOR = { student:'#10b981', teacher:'#1e90ff', sales:'#8b5cf6', admin:'#e87c1e' }

function timeAgo(ts) {
  const s = (Date.now() - new Date(ts).getTime()) / 1000
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return new Date(ts).toLocaleDateString('en-IN', { day:'2-digit', month:'short' })
}

export default function AdminSupportInbox({ profile }) {
  const [messages, setMessages] = useState([])
  const [people,   setPeople]   = useState({})
  const [loading,  setLoading]  = useState(true)
  const [err,      setErr]      = useState('')
  const [openRoom, setOpenRoom] = useState(null)
  const [reply,    setReply]    = useState('')
  const [sending,  setSending]  = useState(false)
  const [filter,   setFilter]   = useState('waiting')
  const bottomRef = useRef(null)

  async function load() {
    setErr('')
    const { data, error } = await supabase
      .from('chat_messages')
      .select('id, room, sender_id, content, created_at')
      .like('room', 'support_%')
      .order('created_at', { ascending: true })
      .limit(2000)
    if (error) { setErr(error.message); setLoading(false); return }
    const support = (data || []).filter(m => m.room?.startsWith('support_'))
    setMessages(support)
    const ids = [...new Set(support.map(m => m.room.slice('support_'.length)))]
    if (ids.length) {
      const { data: ps } = await supabase.from('profiles')
        .select('id, full_name, email, role, student_id').in('id', ids)
      setPeople(Object.fromEntries((ps || []).map(p => [p.id, p])))
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
    if (!AUTO_REFRESH_ENABLED) return
    const channel = supabase.channel('admin-support-inbox')
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'chat_messages' }, payload => {
        if (!payload.new.room?.startsWith('support_')) return
        setMessages(prev => prev.some(m => m.id === payload.new.id) ? prev : [...prev, payload.new])
        const uid = payload.new.room.slice('support_'.length)
        setPeople(prev => {
          if (prev[uid]) return prev
          supabase.from('profiles').select('id, full_name, email, role, student_id').eq('id', uid).single()
            .then(({ data }) => data && setPeople(p => ({ ...p, [uid]: data })))
          return prev
        })
      })
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // One conversation per room, newest activity first. "Waiting" means the
  // last message came from the user rather than an admin.
  const byRoom = {}
  for (const m of messages) (byRoom[m.room] = byRoom[m.room] || []).push(m)
  const conversations = Object.entries(byRoom).map(([room, msgs]) => {
    const userId = room.slice('support_'.length)
    const last = msgs[msgs.length - 1]
    return { room, userId, msgs, last, waiting: last.sender_id === userId, person: people[userId] }
  }).sort((a, b) => new Date(b.last.created_at) - new Date(a.last.created_at))
  const waitingCount = conversations.filter(c => c.waiting).length
  const shown = filter === 'waiting' ? conversations.filter(c => c.waiting) : conversations
  const current = conversations.find(c => c.room === openRoom)

  useEffect(() => {
    if (current) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior:'smooth' }), 50)
  }, [openRoom, current?.msgs.length]) // eslint-disable-line react-hooks/exhaustive-deps

  async function sendReply(e) {
    e.preventDefault()
    if (!reply.trim() || !current) return
    setSending(true); setErr('')
    const content = reply.trim()
    const { data, error } = await supabase.from('chat_messages')
      .insert({ sender_id: profile.id, room: current.room, content })
      .select('id, room, sender_id, content, created_at').single()
    if (error) { setErr(`Reply not sent: ${error.message}`); setSending(false); return }
    setMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data])
    setReply('')
    // Let them know there's a reply; a failure here doesn't undo the reply.
    await supabase.from('notifications').insert({
      user_id: current.userId, type: 'support_reply',
      title: '💬 Admin replied to your support message',
      message: content.slice(0, 200), is_read: false,
    })
    setSending(false)
  }

  const card = { background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.08)', borderRadius:'14px' }
  const nameOf = c => c.person?.full_name || c.person?.email || 'Unknown user'

  if (loading) return <div style={{ padding:'2rem', color:'rgba(var(--fg-rgb),var(--a30))', fontSize:'13px' }}>Loading support messages…</div>

  return (
    <div style={{ marginTop:'14px' }}>
      {err && <div style={{ padding:'9px 14px', marginBottom:'12px', background:'rgba(239,68,68,0.1)', border:'0.5px solid rgba(239,68,68,0.3)', borderRadius:'10px', fontSize:'13px', color:'#f87171' }}>{err}</div>}

      <div style={{ display:'grid', gridTemplateColumns: current ? 'minmax(240px, 320px) 1fr' : '1fr', gap:'14px', alignItems:'start' }} className="support-inbox">
        <div style={{ ...card, padding:'10px', display: current && window.innerWidth < 768 ? 'none' : 'block' }}>
          <div style={{ display:'flex', gap:'6px', marginBottom:'10px' }}>
            {[['waiting', `Needs reply (${waitingCount})`], ['all', `All (${conversations.length})`]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setFilter(id)} style={{ flex:1, padding:'7px 10px', borderRadius:'9px', border:'none', cursor:'pointer', fontFamily:'inherit', fontSize:'12px', fontWeight:700,
                background: filter === id ? '#1e90ff' : 'rgba(var(--fg-rgb),0.06)', color: filter === id ? '#fff' : 'rgba(var(--fg-rgb),var(--a50))' }}>{label}</button>
            ))}
          </div>
          {shown.length === 0 ? (
            <div style={{ padding:'2rem 1rem', textAlign:'center', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a30))' }}>
              {filter === 'waiting' ? '✓ All caught up — no messages waiting for a reply.' : 'No support messages yet.'}
            </div>
          ) : shown.map(c => (
            <button key={c.room} type="button" onClick={() => setOpenRoom(c.room)} style={{ display:'block', width:'100%', textAlign:'left', padding:'10px 12px', marginBottom:'4px', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit',
              background: openRoom === c.room ? 'rgba(30,144,255,0.12)' : 'transparent', border: openRoom === c.room ? '0.5px solid rgba(30,144,255,0.3)' : '0.5px solid transparent' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                {c.waiting && <span style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#f4a335', flexShrink:0 }} title="Needs reply" />}
                <span style={{ fontSize:'13px', fontWeight:700, color:'var(--fg-solid)', flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{nameOf(c)}</span>
                <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))', flexShrink:0 }}>{timeAgo(c.last.created_at)}</span>
              </div>
              <div style={{ display:'flex', gap:'6px', alignItems:'center', marginTop:'3px' }}>
                {c.person?.role && <span style={{ fontSize:'9px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', color: ROLE_COLOR[c.person.role] || '#999' }}>{c.person.role}</span>}
                <span style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {c.last.sender_id === c.userId ? '' : 'You: '}{c.last.content}
                </span>
              </div>
            </button>
          ))}
        </div>

        {current && (
          <div style={{ ...card, display:'flex', flexDirection:'column', minHeight:'420px', maxHeight:'70vh' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'12px 16px', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.08)' }}>
              <button type="button" onClick={() => setOpenRoom(null)} aria-label="Back to conversations" style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(var(--fg-rgb),var(--a50))', fontSize:'16px' }}>←</button>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:'14px', fontWeight:700, color:'var(--fg-solid)' }}>{nameOf(current)}</div>
                <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))' }}>
                  {[current.person?.role, current.person?.student_id && `ID ${current.person.student_id}`, current.person?.email].filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'14px 16px', display:'flex', flexDirection:'column', gap:'8px' }}>
              {current.msgs.map(m => {
                const fromUser = m.sender_id === current.userId
                return (
                  <div key={m.id} style={{ alignSelf: fromUser ? 'flex-start' : 'flex-end', maxWidth:'78%' }}>
                    <div style={{ padding:'9px 12px', borderRadius:'12px', fontSize:'13px', lineHeight:1.5, whiteSpace:'pre-wrap', wordBreak:'break-word',
                      background: fromUser ? 'rgba(var(--fg-rgb),0.07)' : 'rgba(30,144,255,0.18)', color:'var(--fg-solid)' }}>{m.content}</div>
                    <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'3px', textAlign: fromUser ? 'left' : 'right' }}>
                      {fromUser ? '' : 'Admin · '}{new Date(m.created_at).toLocaleString('en-IN', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                    </div>
                  </div>
                )
              })}
              <div ref={bottomRef} />
            </div>
            <form onSubmit={sendReply} style={{ display:'flex', gap:'8px', padding:'12px', borderTop:'0.5px solid rgba(var(--fg-rgb),0.08)' }}>
              <textarea id="support-reply" value={reply} onChange={e => setReply(e.target.value)} rows={2} placeholder={`Reply to ${nameOf(current)}…`}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(e) } }}
                style={{ flex:1, padding:'9px 12px', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', color:'var(--fg-solid)', fontSize:'13px', resize:'none', outline:'none', fontFamily:'inherit' }} />
              <button type="submit" disabled={sending || !reply.trim()} style={{ padding:'0 18px', background:'#1e90ff', color:'#fff', border:'none', borderRadius:'10px', fontSize:'13px', fontWeight:700, cursor:'pointer', opacity: sending || !reply.trim() ? 0.5 : 1, fontFamily:'inherit' }}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
